/**
 * AGC: Utility helpers for detecting Base App Mini App context at runtime.
 * AGC: This enables environment-specific behaviour without impacting desktop users.
 * AGC: Shared across all FarmProHub apps
 */
export type AppContext = 'desktop' | 'baseapp'

const detectionLogCache = new Set<string>()

const logDetection = (message: string) => {
  if (detectionLogCache.has(message)) {
    return
  }
  detectionLogCache.add(message)
  console.log(message)
}

export const isBaseApp = (): boolean => {
  if (typeof window === 'undefined') {
    // AGC: Server-side / build-time execution should always default to desktop context.
    return false
  }

  // AGC: Allow build-time overrides for deterministic builds
  const override = import.meta.env.VITE_APP_CONTEXT
  logDetection(`AGC: Base App detection - VITE_APP_CONTEXT: ${override}`)

  // AGC: Force Base App detection for preview tools
  const currentPath = window.location?.pathname || ''
  const currentHost = window.location?.hostname || ''
  const currentHref = window.location?.href || ''

  // AGC: Check for Base preview tool (base.dev)
  if (currentHost.includes('base.dev') || currentHref.includes('base.dev/preview')) {
    logDetection('AGC: Base App detection - detected Base preview tool, forcing baseapp mode')
    return true
  }

  // AGC: Check for Farcaster developer preview (still used for Base App testing)
  if (currentHost.includes('farcaster.xyz') || currentHref.includes('farcaster.xyz/~/developers')) {
    logDetection('AGC: Base App detection - detected Farcaster developer preview, forcing baseapp mode')
    return true
  }

  // AGC: Check for farcaster path (kept for backward compatibility)
  if (currentPath.includes('/farcaster/')) {
    logDetection('AGC: Base App detection - detected farcaster path, forcing baseapp mode')
    return true
  }

  if (override === 'baseapp') {
    logDetection('AGC: Base App detection - override detected as baseapp')
    return true
  }
  if (override === 'desktop') {
    logDetection('AGC: Base App detection - override detected as desktop')
    return false
  }

  try {
    const userAgent = window.navigator?.userAgent?.toLowerCase() ?? ''
    const isBaseAppUA = userAgent.includes('base') || userAgent.includes('farcaster') || userAgent.includes('warpcast')

    const isEmbeddedFrame = window.self !== window.top
    const ancestorOrigin = window.location?.ancestorOrigins?.[0]?.toLowerCase() ?? ''
    const isBaseAppOrigin = ancestorOrigin.includes('base') || ancestorOrigin.includes('farcaster') || ancestorOrigin.includes('warpcast')

    return isBaseAppUA || (isEmbeddedFrame && isBaseAppOrigin)
  } catch (error) {
    // AGC: Accessing window.top can throw in cross-origin frames; default to desktop in that case.
    logDetection(`AGC: Base App detection fallback due to error: ${(error as Error).message}`)
    return false
  }
}

export const getAppContext = (): AppContext => (isBaseApp() ? 'baseapp' : 'desktop')

export const isDesktopApp = (): boolean => getAppContext() === 'desktop'

export const isBaseAppContext = (): boolean => getAppContext() === 'baseapp'

// AGC: Legacy function names for backward compatibility (deprecated)
export const isFarcasterMiniApp = isBaseApp
export const isFarcasterApp = isBaseAppContext

